// WHAT: Component displaying additional roadmap metrics (high effort, in progress, completed)
// WHY:  Provides visual representation of roadmap status metrics for analytics dashboard
// DOMAIN: Admin → Roadmap → Analytics
// DEPENDENCIES: Card, Badge components, Lucide icons
// RISKS:
//   - NOTE: Dynamic Tailwind classes (bg-${color}/10) may not work with JIT compiler
//   - NOTE: Uses index as key in map - consider using unique identifier

import { LucideAlertTriangle, LucideClock, LucideCheckCircle } from "lucide-react";
import React from "react";

import { Card, Badge } from "@/components/ui";

/**
 * Props for AdditionalMetrics component
 */
interface AdditionalMetricsProperties {
  /** Number of completed items */
  completed: number;
  /** Number of high effort items */
  highEffort: number;
  /** Number of items in progress */
  inProgress: number;
}

const getVariantColor = (variant: string) => {
  if (variant === "destructive") {
    return "red-500";
  }
  if (variant === "primary") {
    return "tracq-bright-blue";
  }
  return "green-500";
};

/**
 * WHAT:
 *   Component that displays additional roadmap metrics in a grid of cards
 *   showing high effort, in progress, and completed item counts.
 *
 * WHY:
 *   Provides quick visual overview of roadmap status metrics for analytics dashboard.
 *
 * PROPS:
 *   - highEffort: number — Count of high effort items
 *   - inProgress: number — Count of items currently in progress
 *   - completed: number — Count of completed items
 *
 * BEHAVIOR:
 *   - Renders three metric cards in responsive grid (1 column mobile, 3 columns desktop)
 *   - Each card shows icon, label badge, and value
 *   - Uses variant colors (destructive, primary, success) for visual distinction
 *
 * RISKS:
 *   - NOTE: Dynamic Tailwind classes may not be purged correctly - use static classes
 *   - NOTE: Index used as key - should use unique identifier if metrics array changes
 *
 * @param root0
 *
 * @param root0.highEffort
 *
 * @param root0.inProgress
 *
 * @param root0.completed
 *
 * @returns {JSX.Element} Die gerenderte AdditionalMetrics-Komponente
 *
 */
export const AdditionalMetrics: React.FC<AdditionalMetricsProperties> = ({
  completed,
  highEffort,
  inProgress,
}) => {
  const metrics = [
    {
      icon: LucideAlertTriangle,
      label: "High Effort",
      value: highEffort,
      variant: "destructive" as const,
    },
    {
      icon: LucideClock,
      label: "In Progress",
      value: inProgress,
      variant: "primary" as const,
    },
    {
      icon: LucideCheckCircle,
      label: "Completed",
      value: completed,
      variant: "success" as const,
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {metrics.map((metric) => {
        const IconComponent = metric.icon;
        const variantColor = getVariantColor(metric.variant);
        return (
          <Card className="text-center" key={metric.label} padding="md" variant="default">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg bg-${variantColor}/10`}>
                <IconComponent className={`w-5 h-5 text-${variantColor}`} />
              </div>
              <div className="text-left">
                <Badge className="mb-1" size="sm" style="subtle" variant={metric.variant}>
                  {metric.label}
                </Badge>
                <div className="text-xl font-bold text-white">{metric.value}</div>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
};
